import React from "react";
import { HelpCircle, Trophy } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const DIVIDER = { borderLeft: "1px solid rgba(15,18,38,0.09)" };

const Col = ({ label, hint, pct, short, color, met, idPrefix }) => (
  <div className="flex-1 min-w-0 px-4 flex flex-col justify-center gap-1.5" style={DIVIDER}>
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[11.5px] text-ink-2 truncate inline-flex items-center gap-1" title={hint}>
        {label} <HelpCircle size={10} className="text-ink-3 shrink-0" />
      </span>
      <span className="font-display text-[13px] font-bold tabular-nums shrink-0" data-testid={`${idPrefix}-short`}
        style={{ color: met ? "#0FB999" : "#0F1226" }}>
        {met ? "met" : money(short)}
        <span className="ml-1.5 text-[11px] font-sans font-semibold" style={{ color }}>{pct}%</span>
      </span>
    </div>
    <div className="h-[6px] rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.09)" }}>
      <div data-testid={`${idPrefix}-bar`} data-pct={pct} className="h-full rounded-full"
        style={{ width: `${Math.max(pct, 2)}%`, background: color, transition: "width 700ms cubic-bezier(.2,.8,.2,1)" }} />
    </div>
  </div>
);

/** CaryPact's "Level Rules" card, on one line beside the greeting. */
export default function LevelProgress({ level }) {
  if (!level) return null;
  const { rank, next, at_top, weight, personal, community, wallet } = level;

  return (
    <div className="glass flex-1 min-w-0 px-4 py-2.5 flex items-stretch flex-wrap md:flex-nowrap gap-y-2"
      data-testid="level-progress-card">
      <div className="pr-4 flex flex-col justify-center gap-1 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          Level rules{wallet ? <span className="normal-case tracking-normal text-ink-3 font-sans"> · {wallet}</span> : null}
        </span>
        <div className="flex items-center gap-2">
          {at_top ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12px] font-bold"
              style={{ background: "rgba(15,185,153,0.12)", color: "#0B8F78" }} data-testid="level-at-top">
              <Trophy size={12} /> Top level
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12.5px] font-bold"
              style={{ background: "rgba(141,28,235,0.1)", color: "#8D1CEB" }} data-testid="level-next">
              {rank || "Not ranked"} <span style={{ color: "rgba(15,18,38,0.35)" }}>→</span> {next}
            </span>
          )}
          {weight != null && (
            <span className="text-[11px] text-ink-3 whitespace-nowrap" data-testid="level-weight"
              title="Your community-reward weight at this level — CaryPact splits the level pool by weight.">
              weight <b className="text-ink font-display text-[12px]">{weight}</b>
            </span>
          )}
        </div>
      </div>

      {at_top ? (
        <div className="flex-1 min-w-0 px-4 flex items-center text-[12px] text-ink-2" style={DIVIDER}>
          There's no higher level to chase — you're at the top of the CaryPact ladder.
        </div>
      ) : (
        <>
          <Col idPrefix="level-personal" label="Personal lock"
            hint={`Personal performance still lacking — your own locked stake. ${money(personal.need)} keeps you on the ladder and you have ${money(personal.have)}.`}
            pct={personal.pct} short={personal.short} met={personal.short === 0} color="#0FB999" />
          <Col idPrefix="level-community" label="Community leg"
            hint={`Community effective performance still needed — ${next} wants ${money(community.need)} in your weaker leg. It counts ${money(community.have)} today.`}
            pct={community.pct} short={community.short} met={community.short === 0} color="#8D1CEB" />
        </>
      )}
    </div>
  );
}
